import { Pagination } from '@/constants/pagination';
import { PaginationAPI } from '@/types/pagination';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type OrdersState = {
  dataStatistics: OrdersStatisticsType;
  pagination: PaginationAPI;
  totalElements?: number;
  dataOrders: OrdersType[];
  dataOrderDetail?: OrderDetailType;
  dataOrderCategory?: OrderDetailCategory;
  dataPublishedOutputs?: OrderItemPublishedOutputs;
  dataOrderItemAuditTrails?: OrderItemAuditTrail[];
  auditTrailsPagination: PaginationAPI;
  dataOrdersubmissionReviewTab?: FieldSubmissionReviewTab[];
  dataSummarySubmissionReviewTab?: SummarySubmissionReviewTab;
  dataSubmissionReviewTabDetail?: SubmissionReviewTabDetailType;
  dataStatisticsResultDraftTab?: ResultDraftTabType;
  dataResultPublishPreview?: ResultPublishPreviewType;
  dataOrdersCategoryTimeline?: OrdersCategoryTimelineType;
};

export const ordersInitialState: OrdersState = {
  dataStatistics: {
    dateFromUtc: '',
    dateToUtc: '',
    totalOrders: 0,
    totalAmount: 0,
    currency: '',
    averageOrderAmount: 0,
    awaitingPaymentCount: 0,
    awaitingPaymentAmount: 0,
    actionRequiredCount: 0,
    actionRequiredAmount: 0,
    inProgressCount: 0,
    inProgressAmount: 0,
    completedCount: 0,
    completedAmount: 0,
    cancelledCount: 0,
    cancelledAmount: 0,
  },
  pagination: {
    pageNumber: Pagination.pageNumber,
    totalPages: 0,
    totalElements: 0,
    pageSize: Pagination.pageSize,
  },
  auditTrailsPagination: {
    pageNumber: Pagination.pageNumber,
    totalPages: 0,
    totalElements: 0,
    pageSize: 10,
  },
  dataOrders: [],
};

export const slice = createSlice({
  name: 'orders',
  initialState: ordersInitialState,
  reducers: {
    setListOrdersStatisticsAction(
      state,
      { payload }: PayloadAction<{ dataStatistics: OrdersStatisticsType }>,
    ) {
      state.dataStatistics = payload.dataStatistics;
    },

    setListOrdersAction(
      state,
      {
        payload,
      }: PayloadAction<{
        data: OrdersType[];
        pagination?: PaginationAPI;
        totalElements?: number;
      }>,
    ) {
      state.dataOrders = payload.data;
      state.pagination = payload.pagination || state.pagination;
      state.totalElements = payload.pagination?.totalElements;
    },

    setListOrdersDetailAction(
      state,
      { payload }: PayloadAction<{ dataOrderDetail: OrderDetailType }>,
    ) {
      state.dataOrderDetail = payload.dataOrderDetail;
    },

    setListOrdersCategorylAction(
      state,
      { payload }: PayloadAction<{ dataOrderCategory: OrderDetailCategory }>,
    ) {
      state.dataOrderCategory = payload.dataOrderCategory;
    },

    setOrderItemPublishedOutputsAction(
      state,
      { payload }: PayloadAction<{ dataPublishedOutputs?: OrderItemPublishedOutputs }>,
    ) {
      state.dataPublishedOutputs = payload.dataPublishedOutputs;
    },

    setOrderItemAuditTrailsAction(
      state,
      {
        payload,
      }: PayloadAction<{
        data?: OrderItemAuditTrail[];
        pagination?: PaginationAPI;
        append?: boolean;
      }>,
    ) {
      state.dataOrderItemAuditTrails = payload.append
        ? [...(state.dataOrderItemAuditTrails || []), ...(payload.data || [])]
        : payload.data;
      state.auditTrailsPagination = payload.pagination || state.auditTrailsPagination;
    },

    setListOrdersSubmissionReviewTabAction(
      state,
      { payload }: PayloadAction<{ dataOrdersubmissionReviewTab: FieldSubmissionReviewTab[] }>,
    ) {
      state.dataOrdersubmissionReviewTab = payload.dataOrdersubmissionReviewTab;
    },

    setListSummarySubmissionReviewTabAction(
      state,
      { payload }: PayloadAction<{ dataSummarySubmissionReviewTab: SummarySubmissionReviewTab }>,
    ) {
      state.dataSummarySubmissionReviewTab = payload.dataSummarySubmissionReviewTab;
    },

    setSubmissionReviewTabDetailAction(
      state,
      { payload }: PayloadAction<{ dataSubmissionReviewTabDetail: SubmissionReviewTabDetailType }>,
    ) {
      state.dataSubmissionReviewTabDetail = payload.dataSubmissionReviewTabDetail;
    },

    setResultDraftTabTabDetailAction(
      state,
      { payload }: PayloadAction<{ dataStatisticsResultDraftTab: ResultDraftTabType }>,
    ) {
      state.dataStatisticsResultDraftTab = payload.dataStatisticsResultDraftTab;
    },

    setResultPublishPreviewAction(
      state,
      { payload }: PayloadAction<{ dataResultPublishPreview: ResultPublishPreviewType }>,
    ) {
      state.dataResultPublishPreview = payload.dataResultPublishPreview;
    },

    setOrdersCategoryTimelineAction(
      state,
      { payload }: PayloadAction<{ dataOrdersCategoryTimeline: OrdersCategoryTimelineType }>,
    ) {
      state.dataOrdersCategoryTimeline = payload.dataOrdersCategoryTimeline;
    },
  },
});

export const {
  setListOrdersStatisticsAction,
  setListOrdersAction,
  setListOrdersDetailAction,
  setListOrdersCategorylAction,
  setOrderItemPublishedOutputsAction,
  setOrderItemAuditTrailsAction,
  setListOrdersSubmissionReviewTabAction,
  setListSummarySubmissionReviewTabAction,
  setSubmissionReviewTabDetailAction,
  setResultDraftTabTabDetailAction,
  setResultPublishPreviewAction,
  setOrdersCategoryTimelineAction,
} = slice.actions;

export default slice.reducer;
